import React, { useEffect, useState } from "react";
import { Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Reusable admin category manager for news/reports/custom-reports.
 * Props:
 *   endpoint: base URL (e.g. "/news/categories", "/reports/categories")
 *   title:    section title
 *   subtitle: caption below title
 *   onChange: (optional) called after add/delete so parent can refresh combobox
 *   confirmDelete: (state, {onConfirm}) => void  — parent's ConfirmDialog setter
 */
export default function CategoryManager({ endpoint, title, subtitle, onChange, confirmDelete }) {
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(endpoint);
      setCategories(data);
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { load(); }, [endpoint]);

  const add = async () => {
    const name = newCat.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post(endpoint, { name });
      toast.success(`Categoria "${name}" aggiunta`);
      setNewCat("");
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const remove = (name) => {
    const doDelete = async () => {
      try {
        await api.delete(`${endpoint}/${encodeURIComponent(name)}`);
        toast.success("Categoria rimossa");
        await load();
        onChange?.();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Errore");
      }
    };
    if (confirmDelete) {
      confirmDelete({
        title: "Rimuovere questa categoria?",
        message: `"${name}" verrà rimossa dall'elenco (gli elementi esistenti mantengono il nome).`,
        confirmLabel: "Rimuovi",
        variant: "danger",
        onConfirm: doDelete,
      });
    } else {
      doDelete();
    }
  };

  return (
    <div className="card-elevated p-5" data-testid={`cat-manager-${endpoint.replace(/\//g, "-")}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display font-semibold text-slate-900">{title}</div>
          {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
        <div className="flex items-center gap-2">
          <input
            value={newCat}
            onChange={(e) => setNewCat(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder="Nuova categoria…"
            className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500 w-48"
            disabled={busy}
            maxLength={40}
            data-testid="cat-new-input"
          />
          <button
            onClick={add}
            disabled={busy || !newCat.trim()}
            className="btn-brand !py-1.5 !px-3 text-sm flex items-center gap-1 disabled:opacity-40"
            data-testid="cat-add-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Aggiungi
          </button>
        </div>
      </div>
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-4">
          {categories.map((c) => (
            <span
              key={c.name}
              className="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700"
              data-testid={`cat-chip-${c.name}`}
            >
              {c.name}
              <span className="text-slate-400 font-normal">({c.count})</span>
              <button
                onClick={() => remove(c.name)}
                className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity"
                title={c.count > 0 ? `Non eliminabile (${c.count} elementi usano questa categoria)` : "Rimuovi"}
                data-testid={`cat-remove-${c.name}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * Read-only chip selector for client filter pages.
 * Props: categories (list of {name,count}), activeCat, onChange(name),
 *        totalCount (for the "Tutte" chip)
 */
export function CategoryChipSelector({ categories, activeCat, onChange, totalCount, testId = "cat-chips" }) {
  if (!categories || categories.length === 0) return null;
  const usable = categories.filter((c) => c.count > 0);
  if (usable.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 items-center" data-testid={testId}>
      <button
        type="button"
        onClick={() => onChange("")}
        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${activeCat === "" ? "bg-blue-600 border-blue-600 text-white shadow-sm shadow-blue-500/30" : "bg-white border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-600"}`}
        data-testid={`${testId}-all`}
      >
        Tutte <span className="opacity-70">({totalCount ?? usable.reduce((a, c) => a + c.count, 0)})</span>
      </button>
      {usable.map((c) => {
        const active = (activeCat || "").toLowerCase() === c.name.toLowerCase();
        return (
          <button
            key={c.name}
            type="button"
            onClick={() => onChange(c.name)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-blue-600 border-blue-600 text-white shadow-sm shadow-blue-500/30" : "bg-white border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-600"}`}
            data-testid={`${testId}-${c.name}`}
          >
            {c.name} <span className="opacity-70">({c.count})</span>
          </button>
        );
      })}
    </div>
  );
}
